/**
 * WhatsApp Utilities for NurinV2
 *
 * Handles phone number normalization and wa.me URL generation
 * for Malaysian phone numbers.
 */

/**
 * Normalize phone number to WhatsApp format (60XXXXXXXXX)
 *
 * Handles various Malaysian phone formats:
 * - +60123456789 → 60123456789
 * - 60123456789 → 60123456789
 * - 0123456789 → 60123456789
 * - 123456789 → 60123456789
 *
 * @param phone - Raw phone number string
 * @returns Normalized phone number or null if invalid
 */
export function normalizePhoneForWhatsApp(
  phone: string | null | undefined
): string | null {
  if (!phone) return null

  // Remove all non-digits
  let cleaned = phone.replace(/\D/g, '')

  // Handle different prefixes
  if (cleaned.startsWith('60')) {
    // Already has country code - good
  } else if (cleaned.startsWith('0')) {
    // Replace leading 0 with 60
    cleaned = '60' + cleaned.substring(1)
  } else if (cleaned.length === 9 || cleaned.length === 10) {
    // Assume Malaysian number without prefix (e.g., 123456789)
    cleaned = '60' + cleaned
  }

  // Validate length (Malaysian mobile: 60 + 9-10 digits = 11-12 total)
  if (cleaned.length < 11 || cleaned.length > 12) {
    return null // Invalid
  }

  // Basic validation: should start with 60
  if (!cleaned.startsWith('60')) {
    return null
  }

  return cleaned
}

/**
 * Generate WhatsApp URL (wa.me link)
 *
 * @param phone - Normalized phone number (60XXXXXXXXX format)
 * @param message - Optional pre-filled message
 * @returns wa.me URL string
 */
export function getWhatsAppUrl(phone: string, message?: string): string {
  const baseUrl = `https://wa.me/${phone}`
  if (message) {
    return `${baseUrl}?text=${encodeURIComponent(message)}`
  }
  return baseUrl
}

/**
 * Format phone number for display
 *
 * @param phone - Phone number string
 * @returns Formatted string like "012-345 6789"
 */
export function formatPhoneDisplay(phone: string | null | undefined): string {
  if (!phone) return '-'

  const normalized = normalizePhoneForWhatsApp(phone)
  if (!normalized) return phone // Return original if can't normalize

  // Remove 60 prefix and format
  const local = normalized.substring(2)

  if (local.length === 10) {
    // Mobile: 012-345 6789
    return `0${local.slice(0, 2)}-${local.slice(2, 5)} ${local.slice(5)}`
  } else if (local.length === 9) {
    // Mobile: 012-345 678
    return `0${local.slice(0, 2)}-${local.slice(2, 5)} ${local.slice(5)}`
  }

  return phone
}

/**
 * Check if a phone number is valid for WhatsApp
 *
 * @param phone - Phone number string
 * @returns true if valid
 */
export function isValidWhatsAppNumber(
  phone: string | null | undefined
): boolean {
  return normalizePhoneForWhatsApp(phone) !== null
}
